<template>
  <aside class="admin-sidebar" :class="{ 'admin-sidebar--collapsed': collapsed }" aria-label="Administration">
    <router-link :to="{ name: 'Admincenter' }" custom v-slot="{ href, navigate }">
      <a
        :href="href"
        class="admin-sidebar__brand"
        aria-label="Admin Center öffnen"
        @click="goHome(navigate, $event)"
      >
        <img src="@/assets/casplogo.png" alt="CASP" class="admin-sidebar__logo" />
        <div v-if="!collapsed" class="admin-sidebar__brand-copy">
          <strong>WE PROTECT.</strong>
          <span>Every time. Everywhere.</span>
        </div>
      </a>
    </router-link>

    <nav class="admin-sidebar__nav" aria-label="Administrationsbereiche">
      <template v-for="entry in adminNavigation" :key="entry.key">
        <AdminNavItem
          v-if="entry.routeName"
          :item="entry"
          :collapsed="collapsed"
          @select="$emit('navigate')"
        />
        <AdminNavGroup
          v-else
          :group="entry"
          :open="isOpen(entry)"
          :collapsed="collapsed"
          @toggle="toggleGroup"
          @select="$emit('navigate')"
        />
      </template>
    </nav>

    <div class="admin-sidebar__footer">
      <div v-if="!collapsed && stats?.user" class="admin-sidebar__user">
        <span class="admin-sidebar__user-label">Angemeldet als</span>
        <strong>{{ stats.user.name }}</strong>
      </div>
      <button
        v-if="collapsible && !collapsed"
        type="button"
        class="admin-sidebar__collapse admin-button--ghost"
        :aria-label="'Navigation einklappen'"
        @click="$emit('toggle-collapse')"
      >
        <i class="fa fa-angles-left" aria-hidden="true"></i>
        <span>Einklappen</span>
      </button>
      <button
        v-else-if="collapsible"
        type="button"
        class="admin-sidebar__collapse admin-sidebar__collapse--icon admin-button--ghost"
        aria-label="Navigation ausklappen"
        title="Navigation ausklappen"
        @click="$emit('toggle-collapse')"
      >
        <i class="fa fa-angles-right" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        class="admin-sidebar__logout"
        :class="{ 'admin-sidebar__logout--icon': collapsed }"
        :title="collapsed ? logoutLabel : undefined"
        @click="logout"
      >
        <i class="fa fa-right-from-bracket" aria-hidden="true"></i>
        <span v-if="!collapsed">{{ logoutLabel }}</span>
      </button>
    </div>
  </aside>
</template>

<script>
import { computed, onMounted, reactive, watch } from "vue";
import { useRoute } from "vue-router";
import { useCookies } from "vue3-cookies";
import CASP from "@/CASP";
import AdminNavGroup from "./AdminNavGroup.vue";
import AdminNavItem from "./AdminNavItem.vue";
import { adminNavigation } from "@/admin/adminNavigation";
import { clearAdminSession, useAdminSession } from "@/admin/adminSession";

export default {
  name: "AdminSidebar",
  components: { AdminNavGroup, AdminNavItem },
  props: {
    collapsed: {
      type: Boolean,
      default: false,
    },
    collapsible: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["toggle-collapse", "navigate"],
  setup(props, { emit }) {
    const route = useRoute();
    const { cookies } = useCookies();
    const { stats, loadAdminSession } = useAdminSession();
    const openGroups = reactive({});

    const activeGroupKey = computed(() => {
      const activeGroup = adminNavigation.find((group) => group.key === route.meta.nav);
      if (activeGroup?.items) return activeGroup.key;
      const activeItem = adminNavigation
        .flatMap((group) => group.items || [])
        .find((item) => item.key === route.meta.nav);
      return activeItem ? groupKeyFor(activeItem) : null;
    });

    function groupKeyFor(item) {
      return adminNavigation.find((group) => (group.items || []).includes(item))?.key;
    }

    function isOpen(group) {
      const explicit = openGroups[group.key];
      if (explicit !== undefined) return explicit;
      return Boolean(
        group.key === route.meta.nav ||
        group.items.some((item) => item.key === route.meta.nav)
      );
    }

    function toggleGroup(key) {
      const group = adminNavigation.find((entry) => entry.key === key);
      openGroups[key] = !isOpen(group);
    }

    function goHome(navigate, event) {
      navigate(event);
      emit("navigate");
    }

    const logoutLabel = computed(() => stats.value?.impersonating ? "Ausloggen (Admin)" : "Ausloggen");

    function logout() {
      if (stats.value?.impersonating) {
        const token = cookies.get("caspauth") || cookies.get("authtoken");
        CASP.stopImpersonation(token, (status) => {
          if (status === 200) {
            clearAdminSession();
            window.location = "/admin/employees";
          } else {
            alert("Die Admin-Identität konnte nicht wiederhergestellt werden.");
          }
        });
        return;
      }
      CASP.logoutWeb((status) => {
        if (status === 200) {
          cookies.remove("caspauth");
          cookies.remove("authtoken");
          clearAdminSession();
          window.location = "/forbidden";
        } else if (status === 409) {
          loadAdminSession(true);
          alert("Die Sitzung wird als Admin verwendet. Bitte erneut versuchen.");
        } else {
          alert("Das Ausloggen ist fehlgeschlagen.");
        }
      });
    }

    onMounted(() => {
      if (activeGroupKey.value) openGroups[activeGroupKey.value] = true;
    });

    watch(
      () => route.meta.nav,
      () => {
        if (activeGroupKey.value) openGroups[activeGroupKey.value] = true;
      }
    );

    return { adminNavigation, stats, isOpen, toggleGroup, goHome, logout, logoutLabel };
  },
};
</script>
